import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Book, Sparkles, Info, Compass, Hand, RefreshCw, Brain, Heart, Clock, MapPin, Users, ChevronDown, ChevronUp, AlertTriangle, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module Images
const MODULE_IMAGES = {
  history: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/5048fdb0dd0fbb3d74bc0505af2d6cc60a3b2a9dc84be422feb696cdfa3a4830.png",
  morphology: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/2dbb0554a862d4e8d9f47448181bfd86fd123d681374af23bfc9446b25828ae1.png",
  lines: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/17b1728558761135f30de5686fa1f4716248bda662c6aaec490022bce7b51b6e.png",
  evolution: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/93af1cbdce1f72c9af2375b5fa74c87d79a6dbbf34ecaff0b9f7e94a7da23adf.png",
  responsible: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/1f92ee4a6c15bef9c77216b62a4139b416a8f8c3b7cc3a65cae82ed103cd0600.png",
  wellness: "https://static.prod-images.emergentagent.com/jobs/173232f4-153e-4827-a2e1-198e1e48ee69/images/7ef96d801882c910cc7dd71f5b1be219d79a05afbf6eacc2b27aa8328ab191f5.png"
};

// Timeline Data
const TIMELINE_DATA = [
  { era: "Antiquité", period: "3000 av. J.-C.", location: "Inde, Chine", description: "Premières traces de la chiromancie dans les textes védiques et la tradition chinoise." },
  { era: "Grèce Antique", period: "500 av. J.-C.", location: "Grèce", description: "Aristote et Hippocrate s'intéressent à la lecture des mains." },
  { era: "Moyen Âge", period: "1200-1500", location: "Europe", description: "La chiromancie se répand en Europe via les échanges commerciaux et culturels." },
  { era: "Renaissance", period: "1500-1700", location: "Europe", description: "Période d'étude scientifique et de codification des pratiques." },
  { era: "Ère Moderne", period: "1800-1900", location: "France, Angleterre", description: "Desbarrolles et Cheiro popularisent la chiromancie moderne." },
  { era: "Contemporain", period: "2000+", location: "Monde", description: "Approche culturelle et introspective, outil de développement personnel." }
];

// Famous Figures
const FAMOUS_FIGURES = [
  { name: "Casimir Stanislas d'Arpentigny", period: "1798-1872", contribution: "Classification des types de mains", origin: "France" },
  { name: "Adolphe Desbarrolles", period: "1801-1886", contribution: "Codification moderne de la chiromancie", origin: "France" },
  { name: "Cheiro (William John Warner)", period: "1866-1936", contribution: "Vulgarisation internationale", origin: "Irlande" },
  { name: "Tradition Védique", period: "Millénaire", contribution: "Hasta Samudrika Shastra - science de la main", origin: "Inde" }
];

// Hand Types
const HAND_TYPES = [
  { type: "Main élémentaire", traits: "Large, doigts courts", personality: "Nature instinctive, travailleur manuel" },
  { type: "Main carrée", traits: "Paume carrée, doigts réguliers", personality: "Pragmatique, organisé, fiable" },
  { type: "Main spatulée", traits: "Extrémités élargies des doigts", personality: "Énergique, inventif, aventurier" },
  { type: "Main philosophique", traits: "Doigts noueux, paume longue", personality: "Penseur, analytique, introspectif" },
  { type: "Main conique", traits: "Doigts effilés, forme artistique", personality: "Créatif, sensible, intuitif" },
  { type: "Main psychique", traits: "Longue, fine, délicate", personality: "Idéaliste, spirituel, réceptif" }
];

// Main Lines
const MAIN_LINES = [
  { 
    name: "Ligne de vie", 
    location: "Contourne le mont de Vénus",
    traditional: "Durée de vie, vitalité générale",
    modern: "Vitalité, énergie, qualité de vie, rapport au corps physique",
    note: "Ne prédit pas la longévité, mais reflète la vitalité générale"
  },
  { 
    name: "Ligne de tête", 
    location: "Traverse horizontalement la paume",
    traditional: "Intelligence, capacités mentales",
    modern: "Mode de pensée, approche intellectuelle, prise de décision",
    note: "Indique le style cognitif plutôt que le niveau d'intelligence"
  },
  { 
    name: "Ligne de cœur", 
    location: "Sous les doigts, traverse la paume",
    traditional: "Vie amoureuse, relations",
    modern: "Expression émotionnelle, rapport aux autres, empathie",
    note: "Reflète la façon d'aimer et de se connecter aux autres"
  },
  { 
    name: "Ligne du destin", 
    location: "Verticale au centre de la paume",
    traditional: "Carrière, succès professionnel",
    modern: "Sens du but, direction de vie, évolution personnelle",
    note: "Absente chez certaines personnes, n'indique pas l'absence de réussite"
  }
];

// Wellness Signs
const WELLNESS_SIGNS = [
  { sign: "Ongles fragiles ou striés", observation: "Peut indiquer fatigue ou carences nutritionnelles", advice: "Consulter si persistant" },
  { sign: "Raideur articulaire", observation: "Tension possible ou surcharge physique", advice: "Écouter son corps, prendre des pauses" },
  { sign: "Coloration inhabituelle", observation: "Changement de circulation ou d'oxygénation", advice: "Consulter un professionnel si inquiétude" },
  { sign: "Sécheresse excessive", observation: "Déshydratation possible ou stress", advice: "Hydrater et observer l'évolution" }
];

const ModuleCard = ({ title, icon: Icon, color, image, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-card rounded-3xl border border-border shadow-sm overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-6 flex items-center justify-between ${color} transition-colors`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <h2 className="font-heading text-xl md:text-2xl text-white text-left">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="h-6 w-6 text-white" />
        ) : (
          <ChevronDown className="h-6 w-6 text-white" />
        )}
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-6"
        >
          {image && (
            <div className="mb-6 rounded-2xl overflow-hidden">
              <img 
                src={image} 
                alt={title}
                className="w-full h-48 md:h-64 object-cover"
              />
            </div>
          )}
          {children}
        </motion.div>
      )}
    </motion.div>
  );
};

const KnowledgePage = () => {
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchKnowledge = async () => {
      try {
        const response = await axios.get(`${API}/knowledge/palmistry`);
        setKnowledge(response.data);
      } catch (error) {
        console.error('Error fetching knowledge:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKnowledge();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <Sparkles className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-8 md:p-10 rounded-3xl shadow-xl mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Book className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-3 text-white">
                  Apprendre la Chiromancie
                </h1>
                <p className="text-white/90 text-base md:text-lg mb-4">
                  Un outil culturel, symbolique et introspectif
                </p>
                <p className="text-white/80 text-sm max-w-2xl">
                  L'objectif n'est pas de "prédire l'avenir", mais de proposer une approche éducative 
                  pour comprendre cet héritage culturel ancien et l'utiliser comme outil de réflexion personnelle.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {[
              { icon: Compass, label: "Histoire", color: "bg-amber-100 text-amber-700" },
              { icon: Hand, label: "Comprendre", color: "bg-blue-100 text-blue-700" },
              { icon: RefreshCw, label: "Évolution", color: "bg-teal-100 text-teal-700" },
              { icon: Brain, label: "Approche", color: "bg-purple-100 text-purple-700" },
              { icon: Heart, label: "Bien-être", color: "bg-green-100 text-green-700" }
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-3 rounded-xl text-center`}>
                <item.icon className="h-5 w-5 mx-auto mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Modules */}
        <div className="space-y-6">
          
          {/* Module 1: Histoire */}
          <ModuleCard 
            title="🧭 Module 1 : Découvrir l'histoire" 
            icon={Compass}
            color="bg-gradient-to-r from-amber-500 to-amber-600"
            image={MODULE_IMAGES.history}
            defaultOpen={true}
          >
            <p className="text-muted-foreground mb-6">
              La chiromancie est un héritage culturel ancien présent dans de nombreuses civilisations. 
              Comprendre son histoire permet d'apprécier sa dimension symbolique et culturelle.
            </p>
            
            {/* Timeline */}
            <div className="mb-8">
              <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                Ligne du temps
              </h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-amber-200" />
                <div className="space-y-4">
                  {TIMELINE_DATA.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="relative pl-10"
                    >
                      <div className="absolute left-2 w-4 h-4 rounded-full bg-amber-500 border-2 border-white" />
                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-heading text-sm text-amber-700">{item.era}</span>
                          <span className="text-xs text-amber-600">({item.period})</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-amber-600 mb-2">
                          <MapPin className="h-3 w-3" />
                          {item.location}
                        </div>
                        <p className="text-sm text-amber-900">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Famous Figures */}
            <div>
              <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-amber-600" />
                Grandes figures
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {FAMOUS_FIGURES.map((figure, index) => (
                  <div key={index} className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <h4 className="font-heading text-sm text-amber-800">{figure.name}</h4>
                    <p className="text-xs text-amber-600 mb-2">{figure.period} • {figure.origin}</p>
                    <p className="text-xs text-amber-900">{figure.contribution}</p>
                  </div>
                ))}
              </div>
            </div>
          </ModuleCard>

          {/* Module 2: Comprendre la main */}
          <ModuleCard 
            title="✋ Module 2 : Comprendre la main" 
            icon={Hand}
            color="bg-gradient-to-r from-blue-500 to-blue-600"
            image={MODULE_IMAGES.morphology}
          >
            <p className="text-muted-foreground mb-6 italic">
              "Chaque main raconte une manière d'agir et de penser."
            </p>

            {/* Hand Types */}
            <div className="mb-8">
              <h3 className="font-heading text-lg mb-4">Morphologie de la main</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {HAND_TYPES.map((hand, index) => (
                  <div key={index} className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <h4 className="font-heading text-sm text-blue-800 mb-1">{hand.type}</h4>
                    <p className="text-xs text-blue-600 mb-2">Traits : {hand.traits}</p>
                    <p className="text-xs text-blue-900">{hand.personality}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Lines */}
            <div>
              <h3 className="font-heading text-lg mb-4">Les lignes principales</h3>
              <img 
                src={MODULE_IMAGES.lines} 
                alt="Lignes de la main"
                className="w-full h-48 md:h-64 object-cover rounded-xl mb-4"
              />
              <div className="space-y-4">
                {MAIN_LINES.map((line, index) => (
                  <div key={index} className="bg-card p-5 rounded-xl border border-border">
                    <h4 className="font-heading text-base text-blue-700 mb-2">{line.name}</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      <strong>Localisation :</strong> {line.location}
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-blue-700 mb-1">Tradition :</p>
                        <p className="text-xs text-blue-900">{line.traditional}</p>
                      </div>
                      <div className="bg-teal-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-teal-700 mb-1">Interprétation moderne :</p>
                        <p className="text-xs text-teal-900">{line.modern}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 italic">
                      💡 {line.note}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mounts */}
            {knowledge?.monts && (
              <div className="mt-8">
                <h3 className="font-heading text-lg mb-4">Les monts de la paume</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(knowledge.monts).map(([key, mont]) => (
                    <div key={key} className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                      <h4 className="font-heading text-sm text-blue-800 capitalize mb-1">
                        {key.replace(/_/g, ' ')}
                      </h4>
                      <p className="text-xs text-blue-600 mb-2">
                        Localisation : {mont.localisation}
                      </p>
                      <p className="text-xs text-blue-900">{mont.signification}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ModuleCard>

          {/* Module 3: Lecture évolutive */}
          <ModuleCard 
            title="🔄 Module 3 : Lecture évolutive" 
            icon={RefreshCw}
            color="bg-gradient-to-r from-teal-500 to-teal-600"
            image={MODULE_IMAGES.evolution}
          >
            <div className="bg-teal-50 p-5 rounded-xl border border-teal-200 mb-6">
              <p className="text-sm text-teal-900 font-medium text-center">
                "La main ne fixe pas un destin, elle reflète des dynamiques."
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-heading text-lg">Main gauche vs Main droite</h3>
                <div className="bg-card p-4 rounded-xl border border-border">
                  <h4 className="text-sm font-semibold text-teal-700 mb-2">Main gauche</h4>
                  <p className="text-xs text-muted-foreground">
                    Traditionnellement associée au potentiel inné, aux tendances héritées, 
                    à ce que nous sommes "à la base".
                  </p>
                </div>
                <div className="bg-card p-4 rounded-xl border border-border">
                  <h4 className="text-sm font-semibold text-teal-700 mb-2">Main droite</h4>
                  <p className="text-xs text-muted-foreground">
                    Associée à l'évolution personnelle, aux choix de vie, 
                    à ce que nous avons développé ou transformé.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-heading text-lg">Évolution des lignes</h3>
                <div className="bg-teal-50 p-4 rounded-xl border border-teal-200">
                  <p className="text-sm text-teal-900 mb-3">
                    Les lignes de la main ne sont pas figées. Elles peuvent évoluer avec :
                  </p>
                  <ul className="space-y-2">
                    {[
                      "L'âge et le vieillissement naturel",
                      "Les changements de mode de vie",
                      "Les expériences significatives",
                      "L'activité physique et professionnelle"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-teal-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-card p-4 rounded-xl border-2 border-teal-300">
                  <p className="text-xs text-teal-700 font-medium">
                    💡 C'est pourquoi comparer des lectures dans le temps peut révéler 
                    des évolutions personnelles intéressantes.
                  </p>
                </div>
              </div>
            </div>
          </ModuleCard>

          {/* Module 4: Approche responsable */}
          <ModuleCard 
            title="🧠 Module 4 : Approche responsable" 
            icon={Brain}
            color="bg-gradient-to-r from-purple-500 to-purple-600"
            image={MODULE_IMAGES.responsible}
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                <h3 className="font-heading text-base text-red-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Ce que ce n'est PAS
                </h3>
                <ul className="space-y-2">
                  {[
                    "Un diagnostic médical",
                    "Une prédiction absolue de l'avenir",
                    "Une science validée",
                    "Un outil de décision définitif"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-red-800">
                      <span className="text-red-500">✗</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-green-50 p-5 rounded-xl border border-green-200">
                <h3 className="font-heading text-base text-green-700 mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Ce que c'EST
                </h3>
                <ul className="space-y-2">
                  {[
                    "Un outil de réflexion",
                    "Une invitation à l'introspection",
                    "Un héritage culturel à explorer",
                    "Un support de développement personnel"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-green-800">
                      <span className="text-green-500">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-purple-50 p-5 rounded-xl border border-purple-200">
              <h3 className="font-heading text-base text-purple-700 mb-3">Utiliser la chiromancie pour :</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: "Développement personnel", desc: "Mieux se connaître et identifier ses tendances" },
                  { title: "Prise de conscience", desc: "Observer ses schémas de pensée et d'action" },
                  { title: "Observation de soi", desc: "Porter attention à son corps et son bien-être" }
                ].map((item, i) => (
                  <div key={i} className="bg-white p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-purple-700 mb-1">{item.title}</h4>
                    <p className="text-xs text-purple-900">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </ModuleCard>

          {/* Module 5: Observation bien-être */}
          <ModuleCard 
            title="🏥 Module 5 : Observation bien-être" 
            icon={Heart}
            color="bg-gradient-to-r from-green-500 to-green-600"
            image={MODULE_IMAGES.wellness}
          >
            <div className="bg-green-50 p-4 rounded-xl border border-green-200 mb-6">
              <p className="text-sm text-green-900 text-center italic">
                "Certains signes visibles peuvent inviter à être attentif à son bien-être général."
              </p>
            </div>

            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 mb-6 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                <strong>Attention :</strong> Ces observations ne constituent en aucun cas un diagnostic médical. 
                En cas de doute ou d'inquiétude, consultez toujours un professionnel de santé.
              </p>
            </div>

            <h3 className="font-heading text-lg mb-4">Signes à observer</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {WELLNESS_SIGNS.map((sign, index) => (
                <div key={index} className="bg-card p-4 rounded-xl border border-border">
                  <h4 className="font-heading text-sm text-green-700 mb-2">{sign.sign}</h4>
                  <p className="text-xs text-muted-foreground mb-2">{sign.observation}</p>
                  <p className="text-xs text-green-600 italic">💡 {sign.advice}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-gradient-to-r from-green-100 to-teal-100 p-5 rounded-xl border border-green-200">
              <p className="text-sm text-green-900 text-center">
                <strong>L'observation des mains</strong> peut servir à détecter précocement des signaux faibles, 
                encourager un bilan médical, et renforcer la prévention santé.
              </p>
            </div>
          </ModuleCard>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground mb-6">
            Prêt à explorer votre main avec cette nouvelle perspective ?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              data-testid="start-reading-from-knowledge"
              onClick={() => navigate('/scan')}
              size="lg"
              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Commencer ma lecture
            </Button>
            <Button
              onClick={() => navigate('/chat-general')}
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-base font-semibold"
            >
              Poser une question
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default KnowledgePage;
